import leftArrow from '@/icons/left-arrow-icon-filled.svg';
import rightArrow from '@/icons/right-arrow-icon-filled.svg';
import { createShopifyImageUrl } from '@/utilities/create-shopify-image-url';
import { logInfo } from '@/utilities/log';
import Slider, { CustomArrowProps } from '@ant-design/react-slick';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'preact/hooks';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import CarouselImagePreviewSquare from '../CarouselImagePreviewSquare';

export type TrialImageCarouselProps = {
  imageUrls: string[];
  aspectRatio?: string;
  activeSlide?: number;
};

/**
 * Renders a carousel component for displaying product images.
 *
 * @param customPaging - A custom paging component to display in the carousel. If not provided, the default image preview component is used.
 * @param images - An array of product images.
 * @param featured - The featured image to display when no active image is selected.
 * @param showArrows - Whether to display the arrow buttons for navigating the carousel.
 * @param showPagingOnMobile - Whether to display the paging dots on mobile devices.
 *
 * @example
 * ```tsx
 * <TrialImageCarousel
 *  images={images}
 *  featured={featured}
 *  showArrows={false}
 *  showPagingOnMobile={true}
 *  customPaging={({i, image, isActive}) => (
 *   <MyCustomPagingComponent
 *    image={image}
 *    isActive={isActive}
 *    onClick={() => console.log('Clicked on image', i)}
 *  />
 * )}
 * />
 * ```
 */
export default function TrialImageCarousel({
  imageUrls,
  aspectRatio,
  activeSlide,
}: TrialImageCarouselProps) {
  const [active, setActive] = useState(0);
  const mainImage = imageUrls[active];
  const thumbnails = useRef<Element[]>([]);
  const sliderRef = useRef<Slider | null>(null);

  useEffect(() => {
    if (activeSlide == null) return;
    setActive(activeSlide);
    sliderRef.current?.slickGoTo(activeSlide, true);
  }, [activeSlide]);

  return (
    <div
      className={classNames('relative', {
        'md:pb-20': imageUrls.length <= 1,
      })}
    >
      <Slider
        ref={sliderRef}
        dots={true}
        dotsClass=""
        fade={true}
        easing="ease-in-out"
        infinite={true}
        speed={300}
        arrows={true}
        appendDots={(dots) => (
          <div className="no-scrollbar overflow-x-auto md:mx-0 md:w-full">
            <ul
              className={classNames(
                'mx-auto mt-6 flex w-max list-none gap-x-3 px-2'
              )}
            >
              {dots}
            </ul>
          </div>
        )}
        beforeChange={(_, newIndex) => {
          logInfo('ChangeCarouselImage', {
            index: newIndex,
            url: imageUrls[newIndex],
          });
          setActive(newIndex);
          const activeThumbnail = thumbnails.current[newIndex];
          if (activeThumbnail) {
            activeThumbnail.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center',
            });
          }
        }}
        // A custom paging component can be injected via props
        // Or the default image preview component is used
        customPaging={(i) => (
          <CarouselImagePreviewSquare
            image={{ url: imageUrls[i], alt: '' }}
            isActive={i === active}
            ref={(el: HTMLButtonElement) => (thumbnails.current[i] = el)}
            onClick={() =>
              logInfo('ClickCarouselPreviewImage', {
                index: i,
                url: imageUrls[i],
              })
            }
          />
        )}
        nextArrow={
          <Arrow
            image={{ url: rightArrow, alt: 'Right arrow icon' }}
            buttonClassName={classNames(
              'absolute top-1/2 z-10 md:translate-y-[calc(-50%-3rem)] right-3 h-8 w-8 opacity-80 hover:opacity-100 fast-transition translate-y-[calc(-50%-3rem)]'
            )}
          />
        }
        prevArrow={
          <Arrow
            image={{ url: leftArrow, alt: 'Left arrow icon' }}
            buttonClassName={classNames(
              'absolute top-1/2 left-3 z-10 md:translate-y-[calc(-50%-3rem)] h-8 w-8 opacity-80 hover:opacity-100 fast-transition translate-y-[calc(-50%-3rem)]'
            )}
          />
        }
      >
        {imageUrls.map((image) => (
          <div key={image}>
            <img
              src={createShopifyImageUrl(mainImage, 640)}
              alt={''}
              // eslint-disable-next-line tailwindcss/no-custom-classname
              className="pointer-events-none w-full object-contain"
              style={{ aspectRatio: aspectRatio ?? 'auto' }}
            />
          </div>
        ))}
      </Slider>
    </div>
  );
}

/**
 * Renders a custom arrow component with an image.
 * This is used to override the default buttons in the carousel.
 *
 * @param image - The image object containing the URL and alt text.
 * @param style - The inline style for the arrow component.
 * @returns The rendered arrow component.
 */
function Arrow({ image, buttonClassName, onClick }: ArrowProps) {
  return (
    <button
      className={buttonClassName}
      onClick={onClick as (_: MouseEvent) => void}
    >
      <img
        src={image.url}
        alt={image.alt ?? ''}
        className="size-full object-contain"
      />
    </button>
  );
}

export type CustomPagingProps = {
  i: number;
  image: Image;
  isActive: boolean;
};

type ArrowProps = CustomArrowProps & {
  image: Image;
  buttonClassName: string;
};

type Image = {
  alt: string | null;
  url: string;
};
